/**
 * Renders a little 3D portrait of each part for the tray, using the same views as the board,
 * so the tray always matches what lands on the board.
 */
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import type { PartProps, PartType } from '../core/types';
import { partDef } from '../parts/registry';
import { createView } from './views';

export function renderThumbs(
  items: { key: string; type: PartType; props: PartProps }[],
  size = 168,
): Map<string, string> {
  const out = new Map<string, string>();
  let renderer: THREE.WebGLRenderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  } catch {
    return out;
  }
  renderer.setSize(size, size, false);
  renderer.setPixelRatio(1);
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.6;
  scene.add(new THREE.HemisphereLight('#ffffff', '#b9a8ff', 0.6));
  const key = new THREE.DirectionalLight('#ffffff', 1.8);
  key.position.set(-3, 6, 4);
  scene.add(key);
  const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 100);

  for (const it of items) {
    const props = { ...partDef(it.type).defaultProps(), ...it.props };
    const view = createView({ id: 'thumb', type: it.type, x: 0, y: 0, rot: 0, layer: 0, props });
    const len = partDef(it.type).length(props);
    // Show long parts diagonally so they fit the square.
    view.group.rotation.y = len > 3 ? -Math.PI / 4 : -Math.PI / 6;
    view.update(
      { id: 'thumb', type: it.type, x: 0, y: 0, rot: 0, layer: 0, props },
      { state: partDef(it.type).initState(), reading: { current: 0, voltage: 0 }, input: {}, time: 0, dt: 0 },
    );
    scene.add(view.group);
    const box = new THREE.Box3().setFromObject(view.group);
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const dist = (sphere.radius / Math.sin(THREE.MathUtils.degToRad(14))) * 1.02;
    const dir = new THREE.Vector3(0, 1.1, 1).normalize();
    camera.position.copy(sphere.center).addScaledVector(dir, dist);
    camera.lookAt(sphere.center);
    renderer.render(scene, camera);
    out.set(it.key, renderer.domElement.toDataURL('image/png'));
    scene.remove(view.group);
    view.dispose();
  }
  pmrem.dispose();
  renderer.dispose();
  renderer.forceContextLoss();
  return out;
}
